import React, { useEffect, useState } from "react";
import { CheckCircle2, X } from "lucide-react";
import { submitContactForm } from "../services/api";
import BUSINESS from "../lib/businessInfo.json";

const TIMEZONES = [
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Berlin", label: "Central Europe (CET/CEST)" },
  { value: "America/New_York", label: "US Eastern (ET)" },
  { value: "America/Chicago", label: "US Central (CT)" },
  { value: "America/Denver", label: "US Mountain (MT)" },
  { value: "America/Los_Angeles", label: "US Pacific (PT)" },
  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
  { value: "Pacific/Auckland", label: "Auckland (NZST/NZDT)" },
];

function detectTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && TIMEZONES.some((item) => item.value === tz)) return tz;
    if (tz) return tz;
  } catch {
    /* ignore */
  }
  return "Asia/Kolkata";
}

function buildInitialForm(context = null) {
  return {
    name: "",
    email: "",
    phone: "",
    teacherName: context?.teacher_name || "",
    timezone: detectTimezone(),
    preferredAvailability: "",
    goals: "",
  };
}

const inputClass =
  "w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:border-emerald-400 focus:ring-emerald-100 outline-none text-sm text-gray-900 placeholder-gray-400 transition-colors";

export default function FreeConsultationForm({ open, onClose, context = null }) {
  const [form, setForm] = useState(() => buildInitialForm(context));
  const [website, setWebsite] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const askTeacher = Boolean(context?.askTeacher);

  const knownTeacherName = context?.teacher_name || "";

  useEffect(() => {
    if (!open) return;
    setForm(buildInitialForm({ teacher_name: knownTeacherName }));
    setWebsite("");
    setError("");
    setSubmitted(false);
    setSending(false);
  }, [open, knownTeacherName]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");

    try {
      const teacherName = (form.teacherName || context?.teacher_name || "").trim();
      await submitContactForm({
        form_type: "consultation",
        name: form.name,
        email: form.email,
        phone: form.phone,
        teacher_name: teacherName,
        program: context?.retreat_name || "",
        message: form.goals,
        timezone: form.timezone,
        preferred_availability: form.preferredAvailability,
        retreat_key: context?.retreat_key || "",
        retreat_name: context?.retreat_name || "",
        retreat_slug: context?.slug || "",
        retreat_location: context?.location || "",
        source_url: typeof window !== "undefined" ? window.location.href : "",
        website,
      });
      setSubmitted(true);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        `Something went wrong. Please email us at ${BUSINESS.email} or WhatsApp ${BUSINESS.phoneDisplay}.`;
      setError(message);
    } finally {
      setSending(false);
    }
  };

  const timezoneOptions = TIMEZONES.some((item) => item.value === form.timezone)
    ? TIMEZONES
    : [{ value: form.timezone, label: form.timezone }, ...TIMEZONES];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consultation-form-title"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white shadow-xl border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 bg-gradient-to-r from-emerald-600 to-teal-700 px-5 py-4 text-white">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-100">
              Free · 15 minutes · Online
            </p>
            <h2 id="consultation-form-title" className="text-lg sm:text-xl font-bold mt-0.5">
              Book Free Consultation
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-white/90 hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {submitted ? (
          <div className="px-5 py-10 text-center">
            <CheckCircle2 className="mx-auto mb-3 text-emerald-600" size={48} strokeWidth={1.5} />
            <h3 className="text-xl font-bold text-gray-900">Request received</h3>
            <p className="mt-3 text-sm text-gray-600 leading-relaxed max-w-sm mx-auto">
              Thanks! Our team will contact you to schedule a free 15-minute call with the instructor —
              completely free and with no obligation.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">
            <p className="text-sm text-gray-600 leading-relaxed">
              Three quick details and we will be in touch to arrange your call. Everything else is
              optional — we can settle the timing on the call itself.
            </p>

            {context?.retreat_name && !askTeacher && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-sm text-emerald-900">
                <span className="font-medium">About:</span> {context.retreat_name}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-xs font-semibold text-gray-700 mb-1.5">Full name</span>
                <input
                  type="text"
                  name="name"
                  required
                  autoComplete="name"
                  value={form.name}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Your name"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Phone / WhatsApp
                </span>
                <input
                  type="tel"
                  name="phone"
                  required
                  autoComplete="tel"
                  value={form.phone}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="+91 ..."
                />
              </label>
            </div>

            <label className="block">
              <span className="block text-xs font-semibold text-gray-700 mb-1.5">
                What would you like help with?
              </span>
              <textarea
                name="goals"
                required
                rows={3}
                value={form.goals}
                onChange={handleChange}
                className={inputClass}
                placeholder="e.g. Beginner, want private 1-to-1 sessions for back pain"
              />
            </label>

            <details className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2.5">
              <summary className="cursor-pointer text-xs font-semibold text-gray-700">
                Add scheduling details{" "}
                <span className="font-normal text-gray-500">(optional)</span>
              </summary>

              <div className="mt-3 space-y-4">
                {askTeacher && (
                  <label className="block">
                    <span className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Teacher / instructor name
                    </span>
                    <input
                      type="text"
                      name="teacherName"
                      value={form.teacherName}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="e.g. Kritika Sharma"
                    />
                    <span className="mt-1 block text-xs text-gray-500">
                      Leave blank and we will recommend a teacher for you.
                    </span>
                  </label>
                )}

                <label className="block">
                  <span className="block text-xs font-semibold text-gray-700 mb-1.5">Email</span>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="you@example.com"
                  />
                </label>

                <label className="block">
                  <span className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Your timezone
                  </span>
                  <select
                    name="timezone"
                    value={form.timezone}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    {timezoneOptions.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="block text-xs font-semibold text-gray-700 mb-1.5">
                    When are you available?
                  </span>
                  <textarea
                    name="preferredAvailability"
                    rows={3}
                    value={form.preferredAvailability}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder='e.g. "Any time", "Weekday evenings IST", or "Sat–Sun mornings"'
                  />
                  <span className="mt-1 block text-xs text-gray-500">
                    Sessions can be arranged in your timezone, or whenever you prefer.
                  </span>
                </label>
              </div>
            </details>

            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
            />

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center px-5 py-3 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 shadow-sm shadow-emerald-200"
            >
              {sending ? "Submitting…" : "Submit consultation request"}
            </button>
            <p className="text-center text-xs text-gray-500">
              No obligation to book. Our team will follow up to confirm a time.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
